import { useCallback, useEffect, useRef, useState } from 'react';

export type SnackbarSeverity = 'success' | 'info' | 'warning' | 'error';

export interface UseSnackbarResult {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
  show: (message: string, severity?: SnackbarSeverity, autoHideMs?: number) => void;
  close: () => void;
}

const DEFAULT_AUTO_HIDE_MS = 6000;

/** An error or a warning has something the user must read or act on: it stays until dismissed or replaced. */
export function isPersistentSeverity(severity: SnackbarSeverity): boolean {
  return severity === 'error' || severity === 'warning';
}

export function useSnackbar(defaultAutoHideMs: number = DEFAULT_AUTO_HIDE_MS): UseSnackbarResult {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<SnackbarSeverity>('success');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    clearTimer();
    setOpen(false);
    setMessage('');
  }, [clearTimer]);

  const show = useCallback(
    (nextMessage: string, nextSeverity: SnackbarSeverity = 'success', autoHideMs: number = defaultAutoHideMs) => {
      setMessage(nextMessage);
      setSeverity(nextSeverity);
      setOpen(true);
      clearTimer();
      if (isPersistentSeverity(nextSeverity)) return;
      timerRef.current = setTimeout(() => {
        setOpen(false);
        setMessage('');
        timerRef.current = null;
      }, autoHideMs);
    },
    [clearTimer, defaultAutoHideMs]
  );

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  return { open, message, severity, show, close };
}
